import React, { useState, useEffect } from 'react';
import { User, Loader, Upload, ArrowRight } from 'lucide-react';
import { peopleService } from '../../modules/people/peopleService';
import { uploadService } from '../../lib/uploadService';
import { useAuth } from '../../hooks/useAuth';
import { Person } from '../../types';
import { SkillChipInput } from '../../modules/people/SkillChipInput';

interface ProfileOnboardingProps {
  onComplete: () => void;
}

export const ProfileOnboarding: React.FC<ProfileOnboardingProps> = ({ onComplete }) => {
  const { appUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [existingSkills, setExistingSkills] = useState<{ [category: string]: string[] }>({});
  
  const [formData, setFormData] = useState({
    name: appUser?.displayName || '',
    title: '',
    department: '',
    phone: '',
    bio: '',
    arbeidsoppgaver: '',
    verktoyOgSystemer: [] as string[],
    spesialiserteVerktoyUtvikler: [] as string[],
    spesialiserteVerktoyDesigner: [] as string[],
    spesialiserteVerktoyMarkedsforing: [] as string[],
    spesialiserteVerktoyProsjektledelse: [] as string[],
    spesialiserteVerktoy3D: [] as string[],
    spesialiserteVerktoyAI: [] as string[],
    andreFerdigheter: ''
  });
  const [existingPerson, setExistingPerson] = useState<Person | null>(null);
  
  const [error, setError] = useState('');

  // Load existing skills and existing person data when component mounts
  useEffect(() => {
    loadExistingSkills();
    loadExistingPersonData();
  }, []);

  const loadExistingPersonData = async () => {
    if (!appUser?.email) return;
    
    try {
      const person = await peopleService.getPersonByEmail(appUser.email);
      if (person) {
        setExistingPerson(person);
        // Pre-fill form with existing person data
        setFormData({
          name: person.name || appUser.displayName || '',
          title: person.title || '',
          department: person.department || '',
          phone: person.phone || '',
          bio: person.bio || '',
          arbeidsoppgaver: person.arbeidsoppgaver || '',
          verktoyOgSystemer: person.verktoyOgSystemer || [],
          spesialiserteVerktoyUtvikler: person.spesialiserteVerktoyUtvikler || [],
          spesialiserteVerktoyDesigner: person.spesialiserteVerktoyDesigner || [],
          spesialiserteVerktoyMarkedsforing: person.spesialiserteVerktoyMarkedsforing || [],
          spesialiserteVerktoyProsjektledelse: person.spesialiserteVerktoyProsjektledelse || [],
          spesialiserteVerktoy3D: person.spesialiserteVerktoy3D || [],
          spesialiserteVerktoyAI: person.spesialiserteVerktoyAI || [],
          andreFerdigheter: person.andreFerdigheter || ''
        });
        
        // Set profile picture preview if exists
        if (person.profilePictureUrl) {
          setProfilePreview(person.profilePictureUrl);
        }
      }
    } catch (error) {
      console.error('Failed to load existing person data:', error);
    }
  };

  const loadExistingSkills = async () => {
    try {
      const people = await peopleService.getPeople();
      const skillCategories = {
        verktoyOgSystemer: new Set<string>(),
        spesialiserteVerktoyUtvikler: new Set<string>(),
        spesialiserteVerktoyDesigner: new Set<string>(),
        spesialiserteVerktoyMarkedsforing: new Set<string>(),
        spesialiserteVerktoyProsjektledelse: new Set<string>(),
        spesialiserteVerktoy3D: new Set<string>(),
        spesialiserteVerktoyAI: new Set<string>(),
      };

      people.forEach(person => {
        Object.keys(skillCategories).forEach(category => {
          const skills = person[category as keyof Person] as string[] || [];
          skills.forEach(skill => {
            if (skill.trim()) {
              skillCategories[category as keyof typeof skillCategories].add(skill.trim());
            }
          });
        });
      });

      const skillsSorted = Object.fromEntries(
        Object.entries(skillCategories).map(([category, skillSet]) => [
          category, 
          Array.from(skillSet).sort()
        ])
      );

      setExistingSkills(skillsSorted);
    } catch (error) {
      console.error('Failed to load existing skills:', error);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate the image file
    const validation = uploadService.validateProfileImage(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid image file');
      return;
    }

    setProfileImage(file);
    setError('');
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setProfilePreview(previewUrl);
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setProfilePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!appUser) return;

    if (!formData.name.trim() || !formData.title.trim() || !formData.department.trim() || !formData.phone.trim()) {
      setError('Name, job title, department, and phone are required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      let profilePictureUrl = existingPerson?.profilePictureUrl || '';
      let profilePicturePath = existingPerson?.profilePicturePath || '';

      // Upload profile image if a new one was selected
      if (profileImage) {
        try {
          console.log('Uploading profile image...');
          const uploadResult = await uploadService.uploadProfileImage(profileImage);
          profilePictureUrl = uploadResult.url;
          profilePicturePath = uploadResult.path;
          console.log('Profile image uploaded successfully:', profilePictureUrl);
        } catch (uploadError: any) {
          console.error('Failed to upload profile image:', uploadError);
          setError(`Failed to upload image: ${uploadError.message}`);
          setSubmitting(false);
          return;
        }
      }

      // Prepare skills data
      const skillsData = {
        verktoyOgSystemer: formData.verktoyOgSystemer,
        spesialiserteVerktoyUtvikler: formData.spesialiserteVerktoyUtvikler,
        spesialiserteVerktoyDesigner: formData.spesialiserteVerktoyDesigner,
        spesialiserteVerktoyMarkedsforing: formData.spesialiserteVerktoyMarkedsforing,
        spesialiserteVerktoyProsjektledelse: formData.spesialiserteVerktoyProsjektledelse,
        spesialiserteVerktoy3D: formData.spesialiserteVerktoy3D,
        spesialiserteVerktoyAI: formData.spesialiserteVerktoyAI,
      };

      const personData = {
        email: appUser.email,
        name: formData.name.trim(),
        title: formData.title.trim(),
        department: formData.department.trim(),
        phone: formData.phone.trim(),
        profilePictureUrl,
        profilePicturePath,
        bio: formData.bio.trim(),
        arbeidsoppgaver: formData.arbeidsoppgaver.trim(),
        andreFerdigheter: formData.andreFerdigheter.trim(),
        ...skillsData
      };

      // Update existing person or create new one
      if (existingPerson) {
        await peopleService.updatePerson(existingPerson.id, personData);
      } else {
        await peopleService.addPerson(personData);
      }

      onComplete();
    } catch (error: any) {
      setError(error.message || 'Failed to save profile');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full space-y-8">
        <div className="text-center">
          <User className="mx-auto h-16 w-16 text-primary mb-4" />
          <h2 className="text-3xl font-extrabold text-foreground mb-2">
            {existingPerson ? 'Confirm Your Profile' : 'Complete Your Profile'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {existingPerson 
              ? 'Please review and confirm your profile details. You can make any changes needed.'
              : 'Help your team get to know you better. Name, job title, department, email, and phone are required to continue.'
            }
          </p>
        </div>

        <div className="bg-card shadow rounded-lg p-6 border">
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Image Upload */}
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Profile Picture</label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div 
                    onClick={() => document.getElementById('profile-image-onboarding')?.click()}
                    className="w-20 h-20 rounded-full cursor-pointer hover:opacity-80 transition-opacity border-2 border-border"
                  >
                    {profilePreview ? (
                      <img
                        src={profilePreview}
                        alt="Profile preview"
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors">
                        <Upload size={24} className="text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  
                  {/* Remove Image Button */}
                  {profilePreview && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      ×
                    </button>
                  )}
                </div>
                
                <div>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImageSelect}
                    className="hidden"
                    id="profile-image-onboarding"
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById('profile-image-onboarding')?.click()}
                    className="bg-secondary text-secondary-foreground px-4 py-2 rounded-lg hover:bg-secondary/80 transition-colors border"
                  >
                    Choose Image
                  </button>
                  <p className="text-xs text-muted-foreground mt-1">
                    Optional. JPG, PNG, or WebP up to 5MB. Click avatar or button to select. Click × to remove.
                  </p>
                </div>
              </div>
            </div>

            {/* Basic Information */}
            <div className="space-y-4">
              {/* Full Name - Full width, first */}
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="John Doe"
                  required
                />
              </div>

              {/* Job Title and Department */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">Job Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full p-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="Senior Designer"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">Department *</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                    className="w-full p-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="Oslo, Tønsberg, Stavanger"
                    required
                  />
                </div>
              </div>

              {/* Email and Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">Email</label>
                  <input
                    type="email"
                    value={appUser?.email || ''}
                    disabled
                    className="w-full p-3 border border-border rounded-lg bg-muted text-muted-foreground"
                    placeholder="employee@agency.com"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This links to your user account
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full p-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="+47 123 45 678"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                className="w-full p-3 border border-border rounded-lg h-24 bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Brief professional bio..."
              />
            </div>

            {/* Skills Sections */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Skills & Tools</h3>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">Work Tasks</label>
                <textarea
                  value={formData.arbeidsoppgaver}
                  onChange={(e) => setFormData(prev => ({ ...prev, arbeidsoppgaver: e.target.value }))}
                  className="w-full p-3 border border-border rounded-lg h-20 bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="Describe main work tasks and responsibilities..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SkillChipInput
                  label="Tools and Systems"
                  skills={formData.verktoyOgSystemer}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, verktoyOgSystemer: skills }))}
                  existingSkills={existingSkills.verktoyOgSystemer || []}
                  placeholder="Slack, Google Workspace, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: Developer"
                  skills={formData.spesialiserteVerktoyUtvikler}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyUtvikler: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyUtvikler || []}
                  placeholder="React, Node.js, Python, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: Designer"
                  skills={formData.spesialiserteVerktoyDesigner}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyDesigner: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyDesigner || []}
                  placeholder="Figma, Adobe Creative Suite, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: Marketing"
                  skills={formData.spesialiserteVerktoyMarkedsforing}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyMarkedsforing: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyMarkedsforing || []}
                  placeholder="Google Ads, Facebook Ads, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: Project Management"
                  skills={formData.spesialiserteVerktoyProsjektledelse}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyProsjektledelse: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyProsjektledelse || []}
                  placeholder="Asana, Jira, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: 3D"
                  skills={formData.spesialiserteVerktoy3D}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoy3D: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoy3D || []}
                  placeholder="Blender, Cinema 4D, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: AI"
                  skills={formData.spesialiserteVerktoyAI}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyAI: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyAI || []}
                  placeholder="ChatGPT, Midjourney, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">Other Skills</label>
                <textarea
                  value={formData.andreFerdigheter}
                  onChange={(e) => setFormData(prev => ({ ...prev, andreFerdigheter: e.target.value }))}
                  className="w-full p-3 border border-border rounded-lg h-20 bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="Other skills, certifications, languages, etc."
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={!formData.name.trim() || !formData.title.trim() || !formData.department.trim() || !formData.phone.trim() || submitting}
                className="bg-primary text-primary-foreground px-6 py-2 rounded-lg border border-primary flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90 transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    {profileImage ? 'Uploading...' : (existingPerson ? 'Updating profile...' : 'Creating profile...')}
                  </>
                ) : (
                  <>
                    {existingPerson ? 'Confirm Profile' : 'Complete Profile'}
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center text-xs text-muted-foreground">
            <p>
              You can always update your profile later. Feel free to add as much or as little information as you'd like.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};